import { useState, useEffect } from "react";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import PropertySwitcher from "@/components/PropertySwitcher";
import PropertyTransferSheet from "@/components/PropertyTransferSheet";
import NotificationSettingsSheet from "@/components/NotificationSettingsSheet";
import PrivacySecuritySheet from "@/components/PrivacySecuritySheet";
import HelpSupportSheet from "@/components/HelpSupportSheet";
import HomeHealthReportSheet from "@/components/HomeHealthReportSheet";
import HomeLibrarySheet from "@/components/HomeLibrarySheet";
import HomeHealthScore from "@/components/HomeHealthScore";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useSubscription } from "@/hooks/useSubscription";
import UpgradePlanSheet from "@/components/UpgradePlanSheet";
import { User, Bell, Shield, HelpCircle, LogOut, ChevronRight, Loader2, Settings, Home, Send, Edit2, Lock, Star, Layers, Activity, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import CommunityGroupsCard from "@/components/CommunityGroupsCard";
import AgentCard from "@/components/AgentCard";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Profile = () => {
    const navigate = useNavigate();
    const { session, signOut, isAdmin } = useAuth(); // session contains user
    const user = session?.user;

    const { profile, loading, updateProfile, deleteAccount } = useProfile();
    const { activeProperty, loading: propertiesLoading } = usePropertyContext();
    const [signingOut, setSigningOut] = useState(false);
    const [isPropertySwitcherOpen, setIsPropertySwitcherOpen] = useState(false);
    const [isTransferOpen, setIsTransferOpen] = useState(false);
    const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
    const [isLibraryOpen, setIsLibraryOpen] = useState(false);
    const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isCancelSubscriptionOpen, setIsCancelSubscriptionOpen] = useState(false);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);
    const [tempName, setTempName] = useState("");
    const [tempEmail, setTempEmail] = useState("");

    const { plan, getPlanDisplay, isFeatureLocked } = useSubscription();

    const getPlanCardStyles = (planId) => {
        switch (planId) {
            case 'premium_1':
                return {
                    bgClass: "bg-gradient-to-br from-blue-500/20 via-blue-500/5 to-transparent border border-blue-500/20",
                    iconColor: "text-blue-500",
                    iconBg: "bg-blue-500/10",
                };
            case 'premium_3':
                return {
                    bgClass: "bg-gradient-to-br from-purple-500/20 via-purple-500/5 to-transparent border border-purple-500/20",
                    iconColor: "text-purple-500",
                    iconBg: "bg-purple-500/10",
                };
            case 'premium_5':
                return {
                    bgClass: "bg-gradient-to-br from-indigo-500/20 via-indigo-500/5 to-transparent border border-indigo-500/20",
                    iconColor: "text-indigo-500",
                    iconBg: "bg-indigo-500/10",
                };
            case 'enterprise':
                return {
                    bgClass: "bg-gradient-to-br from-amber-500/20 via-amber-500/5 to-transparent border border-amber-500/20",
                    iconColor: "text-amber-500",
                    iconBg: "bg-amber-500/10",
                };
            default:
                return {
                    bgClass: "bg-gradient-to-br from-slate-500/20 via-slate-500/5 to-transparent border border-slate-500/10",
                    iconColor: "text-slate-500",
                    iconBg: "bg-slate-500/10",
                };
        }
    };

    const getPlanIcon = (planId) => {
        switch (planId) {
            case 'premium_1': return Star;
            case 'premium_3': return Layers;
            case 'premium_5': return Layers;
            case 'enterprise': return Sparkles;
            default: return Star;
        }
    };

    const planStyles = getPlanCardStyles(plan);
    const PlanIcon = getPlanIcon(plan);

    useEffect(() => {
        if (profile) {
            setTempName(profile.full_name || "");
            setTempEmail(user?.email || "");
        } else if (!loading && !profile) {
            // New users must complete onboarding on the index page
            navigate("/app", { replace: true });
        }
    }, [profile, loading, navigate, user?.email]);



    // Manage scroll lock and bottom nav visibility for all sheets in Profile
    useEffect(() => {
        const anySheetOpen = isTransferOpen || isNotificationSettingsOpen || isPrivacyOpen || isHelpOpen || isReportOpen || isLibraryOpen || isUpgradeOpen;
        if (anySheetOpen) {
            document.body.classList.add('popup-open');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.classList.remove('popup-open');
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.classList.remove('popup-open');
            document.body.style.overflow = 'unset';
        };
    }, [isTransferOpen, isNotificationSettingsOpen, isPrivacyOpen, isHelpOpen, isReportOpen, isLibraryOpen, isUpgradeOpen]);

    const handleSaveProfile = async () => {
        if (!tempName.trim()) return;

        let emailUpdated = false;
        if (tempEmail && tempEmail.trim() !== user?.email) {
            const { error } = await supabase.auth.updateUser({ email: tempEmail.trim() });
            if (error) {
                toast.error(`Failed to update email: ${error.message}`);
                return; // Stop here if email fails
            } else {
                emailUpdated = true;
            }
        }

        const success = await updateProfile({
            full_name: tempName
        });
        
        if (success) {
            setIsEditProfileOpen(false);
            if (emailUpdated) {
                toast.success("Profile saved! Please check both your old and new email inboxes to confirm the email change.", { duration: 6000 });
            }
            // If just name changed, updateProfile already toasts "Profile updated"
        }
    };

    useEffect(() => {
        const fetchPendingCount = async () => {
            if (isAdmin) {
                const { count, error } = await supabase
                    .from("providers")
                    .select("*", { count: 'exact', head: true })
                    .eq("is_verified", false);

                if (!error) {
                    setPendingCount(count || 0);
                }
            }
        };

        fetchPendingCount();
    }, [isAdmin]);

    const menuItems = [
        {
            icon: Bell,
            label: "Notifications",
            description: "Configure alerts",
            onClick: () => setIsNotificationSettingsOpen(true)
        },
        {
            icon: Shield,
            label: "Privacy & Security",
            description: "Data and permissions",
            onClick: () => setIsPrivacyOpen(true)
        },
        {
            icon: HelpCircle,
            label: "Help & Support",
            description: "Get assistance",
            onClick: () => setIsHelpOpen(true)
        },
    ];

    const handleSignOut = async () => {
        setSigningOut(true);
        await signOut();
        toast.success("You have been signed out successfully.");
        navigate("/auth");
    };

    const handleDeleteAccount = async () => {
        const success = await deleteAccount();
        if (success) {
            navigate("/auth");
        }
    };

    const handleCancelSubscription = () => {
        const isIos = window.Capacitor?.getPlatform() === 'ios';
        if (isIos) {
            window.location.href = "https://apps.apple.com/account/subscriptions";
        } else {
            window.location.href = "https://play.google.com/store/account/subscriptions";
        }
        setIsCancelSubscriptionOpen(false);
    };

    const userEmail = user?.email ?? "User";
    const isLoading = loading || propertiesLoading;

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            <PageHeader pageName="Profile" />

            {/* Main Content */}
            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 7rem)" }}>
                <div className="max-w-lg mx-auto space-y-6">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : (
                        <>
                            <GlassCard className="p-6 text-center relative group">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
                                    onClick={() => setIsEditProfileOpen(true)}
                                >
                                    <Edit2 className="w-4 h-4" />
                                </Button>
                                <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                                    <User className="w-10 h-10 text-primary" />
                                </div>
                                <h2 className="text-xl font-bold text-foreground">
                                    {profile?.full_name || userEmail.split("@")[0]}
                                </h2>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {userEmail}
                                </p>
                                {isAdmin && (
                                    <span className="inline-block mt-2 text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">
                                        Admin
                                    </span>
                                )}
                            </GlassCard>

                            {/* Upgrade / Subscription Card */}
                            <GlassCard className={cn(
                                "p-5 relative overflow-hidden border-none shadow-xl",
                                planStyles.bgClass
                            )}>
                                <div className="absolute -right-4 -top-4 w-24 h-24 bg-primary/5 rounded-full blur-2xl" />

                                <div className="flex items-center justify-between gap-4 relative">
                                    <div className="flex items-center gap-4">
                                        <div className={cn(
                                            "p-3 rounded-2xl",
                                            planStyles.iconBg,
                                            planStyles.iconColor
                                        )}>
                                            <PlanIcon className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest leading-none mb-1">
                                                Current Plan
                                            </p>
                                            <h3 className="text-lg font-extrabold text-foreground">
                                                {getPlanDisplay()}
                                            </h3>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => setIsUpgradeOpen(true)}
                                            className={cn(
                                                "rounded-xl font-bold h-10 px-6",
                                                plan === 'free' ? "bg-primary shadow-lg shadow-primary/20" : "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 shadow-xl"
                                            )}
                                        >
                                            {plan === 'free' ? 'Upgrade' : 'Manage Plan'}
                                        </Button>
                                        
                                        {plan !== 'free' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setIsCancelSubscriptionOpen(true)}
                                                className="text-xs text-muted-foreground hover:text-destructive h-8 px-2"
                                            >
                                                Cancel Subscription
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {plan === 'free' && (
                                    <p className="text-[10px] text-muted-foreground mt-4 font-medium flex items-center gap-1.5 opacity-80">
                                        <Layers className="w-3 h-3" />
                                        Unlock unlimited home records & service library
                                    </p>
                                )}
                            </GlassCard>

                            {/* Health Report Card */}
                             <GlassCard className="p-4 relative overflow-hidden group active-scale transition-all cursor-pointer">
                                <button
                                    onClick={() => {
                                        if (isFeatureLocked('analytics')) {
                                            setIsUpgradeOpen(true);
                                        } else {
                                            setIsReportOpen(true);
                                        }
                                    }}
                                    className="w-full flex items-center gap-4 relative z-10"
                                >
                                    <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                        <Activity className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-medium text-foreground flex items-center gap-1.5">
                                            Home Health Report
                                             {isFeatureLocked('analytics') && <Lock className="w-3.5 h-3.5 text-amber-500" />}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            View maintenance breakdown
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </button>
                            </GlassCard>

                            {/* Agent Card */}
                            {profile?.agent_id && <AgentCard agentId={profile.agent_id} />}

                            {/* Active Property */}
                            <GlassCard className="p-4">
                                <button
                                    onClick={() => setIsPropertySwitcherOpen(true)}
                                    className="w-full flex items-center gap-4"
                                >
                                    <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                        <Home className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-medium text-foreground">
                                            {activeProperty?.name || "My Home"}
                                        </h3>
                                        <p className="text-sm text-muted-foreground">
                                            {activeProperty?.address || "Tap to switch property"}
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </button>
                            </GlassCard>

                            {/* Home Library */}
                             <GlassCard className="p-4 active-scale transition-all cursor-pointer">
                                <button
                                    onClick={() => setIsLibraryOpen(true)}
                                    className="w-full flex items-center gap-4"
                                >
                                    <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                        <Home className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-medium text-foreground">Home Library</h3>
                                        <p className="text-sm text-muted-foreground">
                                            House plans, meter numbers & docs
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </button>
                            </GlassCard>

                            {/* Property Transfer */}
                            <GlassCard className="p-4">
                                <button
                                    onClick={() => setIsTransferOpen(true)}
                                    className="w-full flex items-center gap-4"
                                >
                                    <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                        <Send className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-medium text-foreground">Transfer Property</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Hand over items to a new owner
                                        </p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                </button>
                            </GlassCard>

                            {/* Admin Link */}
                            {isAdmin && (
                                <GlassCard className="p-4">
                                    <button
                                        onClick={() => navigate("/admin-portal")}
                                        className="w-full flex items-center gap-4"
                                    >
                                        <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                            <Settings className="w-5 h-5 text-primary" />
                                        </div>
                                        <div className="flex-1 text-left relative">
                                            <h3 className="font-medium text-foreground flex items-center gap-2">
                                                Admin Portal
                                                {pendingCount > 0 && (
                                                    <span className="bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                                        {pendingCount}
                                                    </span>
                                                )}
                                            </h3>
                                            <p className="text-sm text-muted-foreground">{pendingCount > 0 ? "New providers awaiting verification" : "Manage providers & verifications"}</p>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                    </button>
                                </GlassCard>
                            )}

                            {/* Menu Items */}
                            <div className="space-y-3">
                                {menuItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <GlassCard key={index} className="p-4">
                                            <button
                                                className="w-full flex items-center gap-4"
                                                onClick={item.onClick}
                                            >
                                                <div className="p-2 rounded-xl bg-primary/10 shrink-0">
                                                    <Icon className="w-5 h-5 text-primary" />
                                                </div>
                                                <div className="flex-1 text-left">
                                                    <h3 className="font-medium text-foreground">{item.label}</h3>
                                                    <p className="text-sm text-muted-foreground">{item.description}</p>
                                                </div>
                                                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                                            </button>
                                        </GlassCard>
                                    );
                                })}
                                {/* Community Groups */}
                                <CommunityGroupsCard />

                                {/* Support Links Card */}
                                <GlassCard className="p-4 bg-muted/30">
                                    <div className="space-y-4">
                                        <button
                                            onClick={() => window.location.href = '/terms'}
                                            className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors px-2"
                                        >
                                            <span>Terms of Service</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                        <div className="h-px bg-border/50 mx-2" />
                                        <button
                                            onClick={() => window.location.href = '/privacy'}
                                            className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors px-2"
                                        >
                                            <span>Privacy Policy</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </GlassCard>

                                {/* Bottom Buttons */}
                                <div className="space-y-3 pt-4 mb-4">
                                    <Button
                                        variant="outline"
                                        className="w-full h-14 rounded-2xl gap-2 font-semibold text-foreground/80"
                                        onClick={handleSignOut}
                                        disabled={signingOut}
                                    >
                                        {signingOut ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <LogOut className="w-5 h-5" />
                                        )}
                                        Sign Out
                                    </Button>

                                    <Button
                                        variant="ghost"
                                        className="w-full h-14 rounded-2xl gap-2 font-semibold text-destructive/60 hover:text-destructive hover:bg-destructive/5"
                                        onClick={() => setIsDeleteDialogOpen(true)}
                                    >
                                        <Shield className="w-5 h-5 opacity-50" />
                                        Delete My Account
                                    </Button>
                                    <p className="text-[10px] text-center text-muted-foreground/60 px-6">
                                        Note: Account deletion is permanent and cannot be undone. All home contents will be wiped.
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main >

            {/* Property Switcher */}
            < PropertySwitcher
                open={isPropertySwitcherOpen}
                onOpenChange={setIsPropertySwitcherOpen}
            />

            {/* Notification Settings */}
            < NotificationSettingsSheet
                open={isNotificationSettingsOpen}
                onOpenChange={setIsNotificationSettingsOpen}
            />

            {/* Privacy & Security */}
            < PrivacySecuritySheet
                open={isPrivacyOpen}
                onOpenChange={setIsPrivacyOpen}
            />

            {/* Help & Support */}
            < HelpSupportSheet
                open={isHelpOpen}
                onOpenChange={setIsHelpOpen}
            />

            {/* Property Transfer */}
            < PropertyTransferSheet
                open={isTransferOpen}
                onOpenChange={setIsTransferOpen}
            />

            {/* Edit Profile Dialog */}
            < Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen} >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Profile</DialogTitle>
                        <DialogDescription>
                            Update your profile information visible to others.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Full Name</Label>
                            <Input
                                id="name"
                                value={tempName}
                                onChange={(e) => setTempName(e.target.value)}
                                placeholder="Enter your full name"
                            />
                        </div>
                        <div className="grid gap-2 mt-2">
                            <Label htmlFor="email">Email Address</Label>
                            <Input
                                id="email"
                                type="email"
                                value={tempEmail}
                                onChange={(e) => setTempEmail(e.target.value)}
                                placeholder="Enter your email"
                            />
                            <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                                Changing your email requires confirmation. You will be sent an approval link to both your old and new email addresses.
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditProfileOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveProfile} disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog >

            <UpgradePlanSheet
                open={isUpgradeOpen}
                onOpenChange={setIsUpgradeOpen}
                currentPlan={plan}
            />

            <HomeHealthReportSheet
                open={isReportOpen}
                onOpenChange={setIsReportOpen}
            />

            {/* Account Deletion Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="sm:max-w-md bg-destructive/5 border-destructive/20">
                    <DialogHeader>
                        <DialogTitle className="text-destructive flex items-center gap-2">
                            <Shield className="w-5 h-5" />
                            Security Protocol: Account Deletion
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            You are about to permanently delete your HomeEzi account.
                            <br /><br />
                            <strong>Warning:</strong> This action is irreversible. All properties, appliance history, documents, and paint codes will be <span className="text-destructive font-bold">wiped forever</span>.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                        <Button
                            variant="outline"
                            className="flex-1 rounded-xl"
                            onClick={() => setIsDeleteDialogOpen(false)}
                            disabled={loading}
                        >
                            Cancel & Stay
                        </Button>
                        <Button
                            variant="destructive"
                            className="flex-1 rounded-xl font-bold"
                            onClick={handleDeleteAccount}
                            disabled={loading}
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                "Delete Everything"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel Subscription Dialog */}
            <Dialog open={isCancelSubscriptionOpen} onOpenChange={setIsCancelSubscriptionOpen}>
                <DialogContent className="sm:max-w-md border-primary/20">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-primary" />
                            Manage Subscription
                        </DialogTitle>
                        <DialogDescription className="pt-2 text-sm leading-relaxed">
                            To cancel or make changes to your Premium subscription, you must manage it through your device settings (App Store or Google Play Store).
                            <br /><br />
                            Would you like to open your device subscription settings now?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
                        <Button
                            variant="default"
                            className="flex-1 rounded-xl font-bold"
                            onClick={() => setIsCancelSubscriptionOpen(false)}
                        >
                            Back
                        </Button>
                        <Button
                            variant="outline"
                            className="flex-1 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/5"
                            onClick={handleCancelSubscription}
                        >
                            Open Settings
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <HomeLibrarySheet
                open={isLibraryOpen}
                onOpenChange={setIsLibraryOpen}
            />
        </div >
    );
};

export default Profile;
